<script setup lang="ts">
    import { computed, inject, ref, watch } from 'vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import DateTimePicker from '@shared/components/DateTimePicker/DateTimePicker.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import EventEditorDetailsNav from '@views/Event/EventEditor/Details/EventEditorDetailsNav.vue';
    import { type Event } from '@views/Event/EventTypes';
    import { normalizeDateTime, useDateTimeField } from '@views/Event/EventEditor/Details/use-date-time-field';
    import { eventKey, queueEventPropertyKey, canEditKey } from '@views/Event/EventEditor/event-editor-keys';

    const props = defineProps<{
        eventId: string;
    }>();

    const event = inject(eventKey)!;
    const canEdit = inject(canEditKey)!;
    const queueEventProperty = inject(queueEventPropertyKey)!;

    type RegistrationProperty = 'RegistrationOpens' | 'RegistrationCloses' | 'LastCancellationDate';

    function useRegistrationField(property: RegistrationProperty, label: string) {
        return { property, label, ...useDateTimeField(() => event.value?.[property]) };
    }

    const opens = useRegistrationField('RegistrationOpens', $Resources.EventRegistrationOpensLabel);
    const closes = useRegistrationField('RegistrationCloses', $Resources.EventRegistrationClosesLabel);
    const lastCancellation = useRegistrationField('LastCancellationDate', $Resources.EventWhenIsLastCancellationLabel);
    const fields = [opens, closes, lastCancellation];

    // ISO "yyyy-MM-ddTHH:mm:ss" strings in the event's timezone compare correctly as plain strings
    const opensAfterCloses = computed(() => !!opens.value.value && !!closes.value.value && opens.value.value >= closes.value.value);
    const closesAfterEnd = computed(() => {
        const end = normalizeDateTime(event.value?.End);
        return !!closes.value.value && !!end && closes.value.value > end;
    });

    const hasError = computed(() => opensAfterCloses.value || closesAfterEnd.value);

    function isBlocked(property: RegistrationProperty) {
        switch (property) {
            case 'RegistrationOpens':
                return opensAfterCloses.value;
            case 'RegistrationCloses':
                return opensAfterCloses.value || closesAfterEnd.value;
            case 'LastCancellationDate':
                return false;
        }
    }

    // Saves every changed field not involved in an error, so a field held back while invalid is persisted once another field fixes it
    function saveAll() {
        for (const field of fields) {
            if (!field.isComplete.value || isBlocked(field.property)) continue;
            // Queued even when equal to the saved value, so stepping back cancels a pending save of the value in between.
            queueEventProperty(field.property, field.value.value ?? '', field.label);
        }
    }

    type RegistrationField = typeof opens;

    // Each checkbox hides its date; an unset date means the checkbox is on (or off, for last update date)
    const opensNow = ref(false);
    const openUntilEnd = ref(false);
    const hasLastUpdateDate = ref(false);
    watch(
        () => event.value?.RegistrationOpens,
        (value) => (opensNow.value = !value),
        { immediate: true }
    );
    watch(
        () => event.value?.RegistrationCloses,
        (value) => (openUntilEnd.value = !value),
        { immediate: true }
    );
    watch(
        () => event.value?.LastCancellationDate,
        (value) => (hasLastUpdateDate.value = !!value),
        { immediate: true }
    );

    function clearField(field: RegistrationField) {
        field.date.value = null;
        field.hour.value = null;
        field.minute.value = null;
        saveAll();
    }

    function toggleOpensNow(checked: boolean) {
        opensNow.value = checked;
        if (checked) clearField(opens);
    }

    function toggleOpenUntilEnd(checked: boolean) {
        openUntilEnd.value = checked;
        if (checked) clearField(closes);
    }

    function toggleLastUpdateDate(checked: boolean) {
        hasLastUpdateDate.value = checked;
        if (!checked) clearField(lastCancellation);
    }
</script>

<template>
    <div v-if="event" class="max-700">
        <h2 class="h3 m-0-t m-lg-b">{{ $Resources.EventWhenIsRegistrationOpenLabel }}?</h2>

        <div class="form-group" :class="opensNow ? 'm-lg-b' : 'm-sm-b'">
            <FormCheckable
                :label="$Resources.EventRegistrationOpenNowToggleLabel"
                :switch-style="SwitchStyle.Standard"
                :checked="opensNow"
                :disabled="!canEdit"
                @update:modelValue="toggleOpensNow" />
        </div>

        <div v-if="!opensNow" class="row">
            <div class="col-md-6">
                <DateTimePicker
                    class="form-group"
                    type="date"
                    :label="$Resources.GenericStartDate"
                    :class="{ 'has-error': opens.isDateMissing.value }"
                    v-model:value="opens.date.value"
                    start-empty
                    clearable
                    :disabled="!canEdit"
                    @update:value="saveAll" />
            </div>
            <div class="col-md-6">
                <DateTimePicker
                    class="form-group"
                    type="time"
                    :label="$Resources.GenericStartTime"
                    :class="{ 'has-error': opens.isTimeMissing.value }"
                    v-model:value_hour="opens.hour.value"
                    v-model:value_minute="opens.minute.value"
                    clearable
                    :disabled="!canEdit"
                    @update:value_hour="saveAll"
                    @update:value_minute="saveAll" />
            </div>
        </div>

        <div class="form-group" :class="openUntilEnd ? 'm-lg-b' : 'm-sm-b'">
            <FormCheckable
                :label="$Resources.EventRegistrationOpenUntilEventEndsToggleLabel"
                :switch-style="SwitchStyle.Standard"
                :checked="openUntilEnd"
                :disabled="!canEdit"
                @update:modelValue="toggleOpenUntilEnd" />
        </div>

        <div v-if="!openUntilEnd" class="row">
            <div class="col-md-6">
                <DateTimePicker
                    class="form-group"
                    type="date"
                    :label="$Resources.GenericEndDate"
                    :class="{ 'has-error': closes.isDateMissing.value }"
                    v-model:value="closes.date.value"
                    start-empty
                    clearable
                    :disabled="!canEdit"
                    @update:value="saveAll" />
            </div>
            <div class="col-md-6">
                <DateTimePicker
                    class="form-group"
                    type="time"
                    :label="$Resources.GenericEndTime"
                    :class="{ 'has-error': closes.isTimeMissing.value }"
                    v-model:value_hour="closes.hour.value"
                    v-model:value_minute="closes.minute.value"
                    clearable
                    :disabled="!canEdit"
                    @update:value_hour="saveAll"
                    @update:value_minute="saveAll" />
            </div>
        </div>

        <BsAlert v-if="hasError" :type="AlertTypes.Danger" :content="$Resources.StartAfterEndError" :dismissible="false" />

        <h2 class="h4 m-0-t">{{ $Resources.EventWhenIsLastCancellationLabel }}?</h2>

        <div class="form-group" :class="hasLastUpdateDate ? 'm-sm-b' : 'm-lg-b'">
            <FormCheckable
                :label="$Resources.EventSetLastCancellationDateToggleLabel"
                :switch-style="SwitchStyle.Standard"
                :checked="hasLastUpdateDate"
                :disabled="!canEdit"
                :description="$Resources.EventLastCancellationDateDescription"
                @update:modelValue="toggleLastUpdateDate" />
        </div>

        <div v-if="hasLastUpdateDate" class="row">
            <div class="col-md-6">
                <DateTimePicker
                    class="form-group"
                    type="date"
                    :label="$Resources.GenericEndDate"
                    :class="{ 'has-error': lastCancellation.isDateMissing.value }"
                    v-model:value="lastCancellation.date.value"
                    start-empty
                    clearable
                    :disabled="!canEdit"
                    @update:value="saveAll" />
            </div>
            <div class="col-md-6">
                <DateTimePicker
                    class="form-group"
                    type="time"
                    :label="$Resources.GenericEndTime"
                    :class="{ 'has-error': lastCancellation.isTimeMissing.value }"
                    v-model:value_hour="lastCancellation.hour.value"
                    v-model:value_minute="lastCancellation.minute.value"
                    clearable
                    :disabled="!canEdit"
                    @update:value_hour="saveAll"
                    @update:value_minute="saveAll" />
            </div>
        </div>

        <EventEditorDetailsNav v-if="event" :eventId="props.eventId" />
    </div>
</template>
